import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import {
  MdLayers,
  MdOutlineViewInAr,
  MdOutlineArchitecture,
  MdCode,
  MdAutoAwesome,
  MdExpandMore,
  MdCheckCircle,
} from 'react-icons/md';
import Markdown from './Markdown';
import ToolChip from './ToolChip';
import InlineArtifactImage from './InlineArtifactImage';
import {
  StepExecutionChip,
  LegacyStepRow,
  LegacyStepNumber,
  LegacyStepContent,
  LegacyStepAgent,
  LegacyStepTask,
} from './Chat.styled';
import {
  AgentBlockWrapper,
  AgentBlockHeader,
  AgentBlockHeaderIcon,
  AgentBlockChevron,
  AgentBlockContent,
  CompactAgentBlockBar,
  CompactAgentBlockCheck,
} from './AgentBlock.styled';
import { mergeSameAgentBoundaries, type Message, type AgentBoundary, type ToolExecution, type InlineArtifact } from '@hooks/useChat';
import type {
  SkillAccentColorFn as AgentAccentColorFn,
  SkillCategoryFn as AgentCategoryFn,
} from '@constants/skillCategories';
import { agentDisplayName, ORCHESTRATOR_AGENT } from '@constants/agentNames';
import { hasStepExec } from './legacyMessage';

/**
 * Renderer for HISTORICAL multi-agent conversations only. New turns never
 * produce agent boundaries or "Executing step" lines (single-agent chat);
 * this module exists so old threads keep rendering. Do not extend it —
 * modern messages render via AssistantContent.
 */

const CATEGORY_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  CAD: MdOutlineViewInAr,
  CAE: MdOutlineArchitecture,
  CAM: MdLayers,
  AI: MdAutoAwesome,
  Coding: MdCode,
};

/** Extract the numeric part from a step ID like "step-1" → "1". */
const stepNumber = (stepId: string): string => {
  const match = /(\d+)/.exec(stepId);
  return match ? match[1] : stepId;
};

/** Build a fresh regex each time to avoid global lastIndex state issues. */
const stepExecPattern = () =>
  /\*\*Executing step ([\w-]+)\*\*\s*\[(\w+)\]:\s*(.+?)(?:\n|$)/g;


/**
 * Split content into plain text and step-execution cards.
 * Returns an array of React nodes.
 *
 * @param keyPrefix  Unique prefix so keys don't collide when this
 *                   function is called multiple times for different
 *                   text segments within the same message.
 */
const renderContentWithSteps = (text: string, keyPrefix = ''): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(stepExecPattern())) {
    const [full, stepId, agent, task] = match;
    const start = match.index ?? 0;

    // Text before this step card
    if (start > lastIndex) {
      const before = text.substring(lastIndex, start).trim();
      if (before) parts.push(<Markdown key={`${keyPrefix}pre-${stepId}`}>{before}</Markdown>);
    }

    parts.push(
      <StepExecutionChip key={`${keyPrefix}step-${stepId}`}>
        <LegacyStepRow>
          <LegacyStepNumber>{stepNumber(stepId)}</LegacyStepNumber>
          <LegacyStepContent>
            <LegacyStepAgent>{agentDisplayName(agent)}</LegacyStepAgent>
            <LegacyStepTask>{task}</LegacyStepTask>
          </LegacyStepContent>
        </LegacyStepRow>
      </StepExecutionChip>,
    );

    lastIndex = start + full.length;
  }

  // Remaining text after last match
  if (lastIndex < text.length) {
    const remaining = text.substring(lastIndex).trim();
    if (remaining) parts.push(<Markdown key={`${keyPrefix}step-tail`}>{remaining}</Markdown>);
  }

  return parts;
};

/**
 * Render a segment of text with interleaved tool chips.
 * Tools are offset-adjusted so positions are relative to the segment start.
 */
const renderSegment = (
  text: string,
  tools: ToolExecution[],
  keyPrefix: string,
  onApprove?: (toolUseId: string) => Promise<void>,
  onReject?: (toolUseId: string, comment?: string) => Promise<void>,
): React.ReactNode[] => {
  if (!tools.length) {
    if (!text) return [];
    if (hasStepExec(text)) return renderContentWithSteps(text, keyPrefix);
    return [<Markdown key={`${keyPrefix}md`}>{text}</Markdown>];
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  tools.forEach((tool, idx) => {
    const pos = tool.insertPosition ?? text.length;
    const toolKey = tool.toolUseId || `${tool.tool}-${idx}`;

    if (pos > cursor) {
      const seg = text.substring(cursor, pos);
      if (hasStepExec(seg)) {
        parts.push(...renderContentWithSteps(seg, `${keyPrefix}s${idx}-`));
      } else {
        parts.push(<Markdown key={`${keyPrefix}t-${toolKey}`}>{seg}</Markdown>);
      }
    }
    parts.push(
      <ToolChip key={`${keyPrefix}c-${toolKey}`} tool={tool} onApprove={onApprove} onReject={onReject} />,
    );
    cursor = pos;
  });

  if (cursor < text.length) {
    const tail = text.substring(cursor);
    if (hasStepExec(tail)) {
      parts.push(...renderContentWithSteps(tail, `${keyPrefix}tail-`));
    } else {
      parts.push(<Markdown key={`${keyPrefix}tail`}>{tail}</Markdown>);
    }
  }

  return parts;
};

interface CollapsibleAgentBlockProps {
  boundary: AgentBoundary;
  accentColorFn?: AgentAccentColorFn;
  categoryFn?: AgentCategoryFn;
  children: React.ReactNode;
}

const CollapsibleAgentBlock: React.FC<CollapsibleAgentBlockProps> = ({ boundary, accentColorFn, categoryFn, children }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const accent = accentColorFn?.(boundary.agent, !!theme.isLightTheme);
  const category = categoryFn?.(boundary.agent);
  const IconComponent = category ? CATEGORY_ICONS[category] : undefined;
  const contentId = `agent-block-${boundary.agent}-${boundary.startPos}`;
  return (
    <AgentBlockWrapper $accentColor={accent}>
      <AgentBlockHeader
        $accentColor={accent}
        $onAccentColor={undefined}
        role="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((e) => !e)}
      >
        {IconComponent && (
          <AgentBlockHeaderIcon>
            <IconComponent size={18} />
          </AgentBlockHeaderIcon>
        )}
        {boundary.displayName}
        <AgentBlockChevron $expanded={expanded}>
          <MdExpandMore size={24} />
        </AgentBlockChevron>
      </AgentBlockHeader>
      <AgentBlockContent
        id={contentId}
        $expanded={expanded}
        data-expanded={expanded}
        aria-hidden={!expanded}
      >
        {children}
      </AgentBlockContent>
    </AgentBlockWrapper>
  );
};

interface CompactAgentBlockProps {
  boundary: AgentBoundary;
  accentColorFn?: AgentAccentColorFn;
  categoryFn?: AgentCategoryFn;
}

const CompactAgentBlock: React.FC<CompactAgentBlockProps> = ({ boundary, accentColorFn, categoryFn }) => {
  const theme = useTheme();
  const accent = accentColorFn?.(boundary.agent, !!theme.isLightTheme);
  const category = categoryFn?.(boundary.agent);
  const IconComponent = category ? CATEGORY_ICONS[category] : undefined;
  return (
    <CompactAgentBlockBar $accentColor={accent} data-testid="compact-agent-block">
      {IconComponent && (
        <AgentBlockHeaderIcon>
          <IconComponent size={18} />
        </AgentBlockHeaderIcon>
      )}
      {boundary.displayName}
      <CompactAgentBlockCheck>
        <MdCheckCircle size={16} />
      </CompactAgentBlockCheck>
    </CompactAgentBlockBar>
  );
};

/**
 * Collect artifacts whose insertPosition falls within [start, end).
 * Artifacts without a position are excluded (handled separately).
 */
const artifactsInRange = (
  artifacts: InlineArtifact[] | undefined,
  start: number,
  end: number,
): InlineArtifact[] => {
  if (!artifacts?.length) return [];
  return artifacts.filter((a) => {
    const pos = a.insertPosition;
    return pos != null && pos >= start && pos < end;
  });
};

type BoundaryApprovalHandlers = {
  onApprove?: (toolUseId: string) => Promise<void>;
  onReject?: (toolUseId: string, comment?: string) => Promise<void>;
};

const boundaryToolKey = (t: ToolExecution): string =>
  t.toolUseId ?? `${t.tool}_${t.insertPosition ?? 0}`;

const byPosition = (a: ToolExecution, b: ToolExecution): number =>
  (a.insertPosition ?? 0) - (b.insertPosition ?? 0);

/**
 * Match tools to an agent boundary by `agentName` tag (set by the Java
 * backend). Untagged tools default to ORCHESTRATOR_AGENT.
 */
const collectToolsForBoundary = (
  boundary: AgentBoundary,
  allTools: ToolExecution[],
  start: number,
  end: number,
  isZeroWidth: boolean,
  claimed: Set<string>,
): ToolExecution[] => {
  const unclaimed = (t: ToolExecution) => !claimed.has(boundaryToolKey(t));
  const posFilter = isZeroWidth
    ? (t: ToolExecution) => (t.insertPosition ?? 0) === start
    : (t: ToolExecution) => {
        const pos = t.insertPosition ?? 0;
        return pos >= start && pos < end;
      };

  return allTools
    .filter(
      (t) =>
        (t.agentName ?? ORCHESTRATOR_AGENT) === boundary.agent &&
        posFilter(t) &&
        unclaimed(t),
    )
    .sort(byPosition);
};

const collectArtifactsForBoundary = (
  allArtifacts: InlineArtifact[],
  start: number,
  end: number,
  isZeroWidth: boolean,
  claimed: Set<string>,
): InlineArtifact[] => {
  if (isZeroWidth) {
    return allArtifacts.filter(
      (a) => a.insertPosition != null && a.insertPosition === start && !claimed.has(a.artifactId),
    );
  }
  return artifactsInRange(allArtifacts, start, end)
    .filter((a) => !claimed.has(a.artifactId));
};

const renderWithBoundaries = (
  content: string,
  tools: ToolExecution[] | undefined,
  boundaries: AgentBoundary[],
  artifacts: InlineArtifact[] | undefined,
  accentColorFn: AgentAccentColorFn | undefined,
  categoryFn: AgentCategoryFn | undefined,
  handlers: BoundaryApprovalHandlers = {},
): React.ReactNode[] => {
  const { onApprove, onReject } = handlers;
  const blocks: React.ReactNode[] = [];
  const merged = mergeSameAgentBoundaries(boundaries, content, tools, artifacts);

  const sorted = merged.length > 0 && merged[0].startPos > 0
    ? [{ agent: 'os', displayName: agentDisplayName('os'), startPos: 0 }, ...merged]
    : merged;

  const claimedToolKeys = new Set<string>();
  const claimedArtifactIds = new Set<string>();
  const allTools = tools ?? [];
  const allArtifacts = artifacts ?? [];

  for (let i = 0; i < sorted.length; i++) {
    const b = sorted[i];
    const isLast = i + 1 >= sorted.length;
    const nextPos = isLast ? content.length + 1 : sorted[i + 1].startPos;
    const segText = content.substring(b.startPos, Math.min(nextPos, content.length));
    const isZeroWidthAgent = b.agent !== ORCHESTRATOR_AGENT && nextPos <= b.startPos;

    // Orchestrator-level tools (e.g. download_attachment) may land inside a
    // sub-agent boundary's positional range — typically at the exact start
    // position — when they fire right before the agent is invoked.  Collect
    // them via the same matching function using a synthetic os boundary,
    // then render inline (no wrapping agent box) before the agent block.
    if (b.agent !== ORCHESTRATOR_AGENT) {
      const osBoundaryProxy: AgentBoundary = { agent: ORCHESTRATOR_AGENT, displayName: '', startPos: b.startPos };
      const escapedOsTools = collectToolsForBoundary(osBoundaryProxy, allTools, b.startPos, nextPos, isZeroWidthAgent, claimedToolKeys);
      if (escapedOsTools.length > 0) {
        escapedOsTools.forEach((t) => { claimedToolKeys.add(boundaryToolKey(t)); });
        blocks.push(
          ...renderSegment('', escapedOsTools.map((t) => ({ ...t, insertPosition: 0 })), `os-esc-${i}-`, onApprove, onReject),
        );
      }
    }

    const rawSegTools = collectToolsForBoundary(b, allTools, b.startPos, nextPos, isZeroWidthAgent, claimedToolKeys);
    rawSegTools.forEach((t) => { claimedToolKeys.add(boundaryToolKey(t)); });
    const segTools = isZeroWidthAgent
      ? rawSegTools.map((t) => ({ ...t, insertPosition: 0 }))
      : rawSegTools.map((t) => ({ ...t, insertPosition: (t.insertPosition ?? 0) - b.startPos }));

    const segArtifacts = collectArtifactsForBoundary(allArtifacts, b.startPos, nextPos, isZeroWidthAgent, claimedArtifactIds);
    segArtifacts.forEach((a) => { claimedArtifactIds.add(a.artifactId); });

    if (b.agent !== ORCHESTRATOR_AGENT && !segText.trim() && segTools.length === 0 && segArtifacts.length === 0) {
      blocks.push(
        <CompactAgentBlock key={`ab-compact-${i}-${b.agent}`} boundary={b} accentColorFn={accentColorFn} categoryFn={categoryFn} />,
      );
      continue;
    }

    if (b.agent === ORCHESTRATOR_AGENT) {
      blocks.push(
        ...renderSegment(segText, segTools, `os-${i}-`, onApprove, onReject),
      );
      segArtifacts.forEach((art) => {
        blocks.push(<InlineArtifactImage key={art.artifactId} artifact={art} />);
      });
    } else {
      blocks.push(
        <CollapsibleAgentBlock key={`ab-${i}-${b.agent}`} boundary={b} accentColorFn={accentColorFn} categoryFn={categoryFn}>
          {renderSegment(segText, segTools, `ag-${i}-`, onApprove, onReject)}
          {segArtifacts.map((art) => (
            <InlineArtifactImage key={art.artifactId} artifact={art} />
          ))}
        </CollapsibleAgentBlock>,
      );
    }
  }

  const unclaimedTools = allTools
    .filter((t) => !claimedToolKeys.has(boundaryToolKey(t)))
    .sort(byPosition);
  if (unclaimedTools.length > 0) {
    unclaimedTools.forEach((t) => { claimedToolKeys.add(boundaryToolKey(t)); });
    blocks.push(
      ...renderSegment('', unclaimedTools.map((t) => ({ ...t, insertPosition: 0 })), 'unclaimed-', onApprove, onReject),
    );
  }

  const unpositioned = allArtifacts.filter((a) => a.insertPosition == null);
  unpositioned.forEach((art) => {
    blocks.push(<InlineArtifactImage key={art.artifactId} artifact={art} />);
  });

  return blocks;
};

interface LegacyAssistantContentProps {
  message: Message;
  agentAccentColor?: AgentAccentColorFn;
  agentCategoryFn?: AgentCategoryFn;
  onApproveToolExecution?: (toolUseId: string) => Promise<void>;
  onRejectToolExecution?: (toolUseId: string, comment?: string) => Promise<void>;
}

const LegacyAssistantContent: React.FC<LegacyAssistantContentProps> = ({ message, agentAccentColor, agentCategoryFn, onApproveToolExecution, onRejectToolExecution }) => {
  const { content, toolExecutions, agentBoundaries } = message;
  const artifacts = message.inlineArtifacts;

  if (agentBoundaries?.length) {
    return (
      <>
        {renderWithBoundaries(
          content, toolExecutions, agentBoundaries, artifacts,
          agentAccentColor, agentCategoryFn,
          { onApprove: onApproveToolExecution, onReject: onRejectToolExecution },
        )}
      </>
    );
  }

  // No boundaries but step-execution lines: interleave tool chips and
  // step cards positionally (same walk as the modern renderer, plus cards).
  const text = content ?? '';
  const parts = renderSegment(
    text,
    [...(toolExecutions ?? [])].sort(
      (a, b) => (a.insertPosition ?? text.length) - (b.insertPosition ?? text.length),
    ),
    'legacy-',
    onApproveToolExecution,
    onRejectToolExecution,
  );
  artifacts?.forEach((art) => {
    parts.push(<InlineArtifactImage key={art.artifactId} artifact={art} />);
  });
  return <>{parts}</>;
};

export default LegacyAssistantContent;
